import { useState, useCallback } from 'react';
import { sendPatientInvite } from '../api/onboardingService';
import type { ExistingPatientMatch } from '../types';
import { useUser } from '@/features/Auth/hooks/useUser';

interface UsePatientInviteReturn {
  sendInvite: (firstName: string, lastName: string, dateOfBirth: string, email: string, mobileNumber: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  success: boolean;
  successMessage: string;
  existingPatient: ExistingPatientMatch | null;
  reset: () => void;
}

export const usePatientInvite = (): UsePatientInviteReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [existingPatient, setExistingPatient] = useState<ExistingPatientMatch | null>(null);
  
  // Get user context for dynamic parameters
  const { portalAccountId, authUserId } = useUser();

  const sendInvite = useCallback(async (
    firstName: string, 
    lastName: string, 
    dateOfBirth: string, 
    email: string,
    mobileNumber: string
  ) => {
    // Don't proceed if we don't have required user context
    if (!portalAccountId || !authUserId) {
      setError('User context not available. Please refresh and try again.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setSuccessMessage('');
      setExistingPatient(null);

      const userContext = {
        portalAccountId,
        userId: authUserId
      };

      console.log('Sending patient invite:', { firstName, lastName, dateOfBirth, email, mobileNumber, userContext });
      
      const result = await sendPatientInvite(firstName, lastName, dateOfBirth, email, mobileNumber, userContext);
      
      console.log('Patient invite result:', result);
      
      if (result.success) {
        if (result.existingPatient) {
          setExistingPatient(result.existingPatient);
          return;
        }

        setSuccess(true);
        setSuccessMessage(result.message);
      } else {
        throw new Error(result.message || 'Failed to send invitation');
      }
    } catch (err) {
      console.error('Error sending patient invite:', err);
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
      setSuccess(false);
      setSuccessMessage('');
      setExistingPatient(null);
    } finally {
      setLoading(false);
    }
  }, [portalAccountId, authUserId]);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
    setSuccessMessage('');
    setExistingPatient(null);
  }, []);

  return {
    sendInvite,
    loading,
    error,
    success,
    successMessage,
    existingPatient,
    reset
  };
};

export default usePatientInvite;

